<template>
  <div class="image-asset-select">
    <button
      type="button"
      class="image-select-entry"
      :class="{ 'is-disabled': disabled, 'has-image': Boolean(selectedItem) }"
      :disabled="disabled"
      @click="openPicker"
    >
      <template v-if="selectedItem">
        <img v-if="accessUrl(selectedItem)" :src="accessUrl(selectedItem)" alt="" />
        <span v-else class="entry-placeholder"><i class="el-icon-picture-outline"></i></span>
        <span class="entry-overlay"><i class="el-icon-refresh"></i>更换图片</span>
      </template>
      <span v-else class="entry-empty">
        <i class="el-icon-plus"></i>
        <strong>{{ buttonText }}</strong>
        <small>从图片资源库选择</small>
      </span>
    </button>

    <div v-if="selectedItem" class="selected-summary">
      <span :title="selectedItem.name || ''">
        {{ selectedItem.name || `图片资源 ${selectedItem.id}` }}
      </span>
      <el-button
        v-if="clearable && !disabled"
        type="text"
        class="clear-button"
        @click="clearSelection"
      >移除</el-button>
    </div>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="920px"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleDialogClosed"
    >
      <div class="picker-toolbar">
        <el-input
          v-model.trim="filters.keyword"
          clearable
          prefix-icon="el-icon-search"
          placeholder="按图片名称搜索"
          @keyup.enter.native="searchFromFirstPage"
          @clear="searchFromFirstPage"
        ></el-input>
        <el-select
          v-model="filters.category_id"
          clearable
          filterable
          placeholder="全部分类"
          @change="searchFromFirstPage"
        >
          <el-option
            v-for="category in categories"
            :key="category.id"
            :label="category.name"
            :value="category.id"
          ></el-option>
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="searchFromFirstPage">搜索</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </div>

      <div v-loading="loading" class="picker-content">
        <div v-if="items.length" class="image-grid">
          <div
            v-for="item in items"
            :key="item.id"
            class="image-card"
            :class="{ 'is-selected': Number(item.id) === draftSelectedId }"
            role="button"
            tabindex="0"
            @click="selectDraft(item)"
            @dblclick="confirmSelection(item)"
            @keyup.enter="selectDraft(item)"
          >
            <div class="image-card__preview">
              <el-image
                v-if="accessUrl(item)"
                :src="accessUrl(item)"
                fit="contain"
                :preview-src-list="[accessUrl(item)]"
                @click.stop
              ></el-image>
              <i v-else class="el-icon-picture-outline"></i>
              <span v-if="Number(item.id) === draftSelectedId" class="selected-mark">
                <i class="el-icon-check"></i>
              </span>
            </div>
            <div class="image-card__meta">
              <strong :title="item.name || ''">{{ item.name || '未命名图片' }}</strong>
              <span>{{ item.category || '未分类' }}</span>
              <small>{{ item.width || 0 }} × {{ item.height || 0 }} · ID {{ item.id }}</small>
            </div>
          </div>
        </div>
        <div v-else-if="!loading" class="empty-result">
          <i class="el-icon-picture-outline"></i>
          <strong>没有找到可选择的图片</strong>
          <span>可以修改名称或分类条件后重新搜索</span>
        </div>
      </div>

      <div class="picker-pagination">
        <span>共 {{ total }} 张图片</span>
        <el-pagination
          background
          layout="prev, pager, next"
          :current-page="pageNum"
          :page-size="pageSize"
          :total="total"
          @current-change="changePage"
        ></el-pagination>
      </div>

      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!draftSelectedItem" @click="confirmSelection()">
          确定选择
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import apis from '@/api/index'

export default {
  name: 'ImageAssetSelect',
  props: {
    value: { type: [Number, String], default: null },
    selected: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    buttonText: { type: String, default: '选择图片' },
    dialogTitle: { type: String, default: '从图片资源库选择' }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      items: [],
      categories: [],
      pageNum: 1,
      pageSize: 6,
      total: 0,
      requestSequence: 0,
      draftSelectedId: null,
      draftSelectedItem: null,
      confirmedItem: null,
      filters: { keyword: '', category_id: '' }
    }
  },
  computed: {
    selectedId() {
      const value = Number(this.value || 0)
      return value > 0 ? value : null
    },
    selectedItem() {
      if (!this.selectedId) return null
      if (this.confirmedItem && Number(this.confirmedItem.id) === this.selectedId) {
        return this.confirmedItem
      }
      return this.selected && Number(this.selected.id) === this.selectedId
        ? this.selected
        : null
    }
  },
  watch: {
    selected: {
      immediate: true,
      handler(value) {
        if (value && Number(value.id) === this.selectedId) this.confirmedItem = value
      }
    },
    value(value) {
      const nextId = Number(value || 0)
      if (!nextId || !this.confirmedItem || Number(this.confirmedItem.id) !== nextId) {
        this.confirmedItem = null
      }
    }
  },
  methods: {
    accessUrl(item) {
      // 2026-09-16：文件节点由服务端决定，管理端只使用完整地址。
      return item && item.full_path ? String(item.full_path) : ''
    },
    async openPicker() {
      if (this.disabled) return
      this.dialogVisible = true
      this.draftSelectedId = this.selectedId
      this.draftSelectedItem = this.selectedItem
      this.pageNum = 1
      await Promise.all([this.loadCategories(), this.loadItems()])
    },
    async loadCategories() {
      try {
        const response = await apis.imageAsset.options()
        if (response.data.code !== 1) throw new Error(response.data.msg || '图片分类加载失败')
        const data = response.data.data || {}
        if (Array.isArray(data.category_options)) {
          this.categories = data.category_options
        } else {
          this.categories = (Array.isArray(data.categories) ? data.categories : [])
            .map((name, index) => ({ id: `legacy-${index}`, name }))
        }
      } catch (error) {
        this.$message.error(String(error.message || error || '图片分类加载失败'))
      }
    },
    async loadItems() {
      const sequence = ++this.requestSequence
      this.loading = true
      try {
        const response = await apis.imageAsset.list({
          keyword: this.filters.keyword,
          category_id: Number(this.filters.category_id) || 0,
          category: String(this.filters.category_id).indexOf('legacy-') === 0
            ? (this.categories.find(item => item.id === this.filters.category_id) || {}).name || ''
            : '',
          status: 1,
          order: 'latest',
          page_num: this.pageNum,
          page_size: this.pageSize
        })
        if (sequence !== this.requestSequence) return
        if (response.data.code !== 1) throw new Error(response.data.msg || '图片资源加载失败')
        const data = response.data.data || {}
        this.items = Array.isArray(data.list) ? data.list : []
        this.total = Number(data.total || 0)
        const selected = this.items.find(item => Number(item.id) === this.draftSelectedId)
        if (selected) this.draftSelectedItem = selected
      } catch (error) {
        if (sequence === this.requestSequence) {
          this.items = []
          this.total = 0
          this.$message.error(String(error.message || error || '图片资源加载失败'))
        }
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    searchFromFirstPage() {
      this.pageNum = 1
      this.loadItems()
    },
    resetFilters() {
      this.filters.keyword = ''
      this.filters.category_id = ''
      this.searchFromFirstPage()
    },
    changePage(page) {
      this.pageNum = Number(page)
      this.loadItems()
    },
    selectDraft(item) {
      this.draftSelectedId = Number(item.id)
      this.draftSelectedItem = item
    },
    confirmSelection(item) {
      if (item) this.selectDraft(item)
      if (!this.draftSelectedItem) return
      const selected = this.draftSelectedItem
      this.confirmedItem = selected
      this.$emit('input', Number(selected.id))
      this.$emit('change', selected)
      this.dialogVisible = false
    },
    clearSelection() {
      this.confirmedItem = null
      this.$emit('input', null)
      this.$emit('change', null)
    },
    handleDialogClosed() {
      this.requestSequence += 1
      this.loading = false
      this.items = []
      this.draftSelectedId = null
      this.draftSelectedItem = null
    }
  }
}
</script>

<style lang="scss" scoped>
.image-asset-select {
  display: inline-flex;
  max-width: 180px;
  flex-direction: column;
  gap: 7px;
  vertical-align: top;
}

.image-select-entry {
  position: relative;
  display: flex;
  width: 168px;
  height: 112px;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px dashed #b8c2d1;
  border-radius: 9px;
  color: #606266;
  background: #f7f9fc;
  cursor: pointer;
  transition: border-color .2s, color .2s, box-shadow .2s;

  &:hover,
  &:focus {
    border-color: #409eff;
    color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, .08);
    outline: none;
  }

  &.has-image { border-style: solid; background: #eef1f5; }
  &.is-disabled { color: #c0c4cc; cursor: not-allowed; box-shadow: none; }

  > img,
  .entry-placeholder { width: 100%; height: 100%; object-fit: contain; }
}

.entry-placeholder,
.entry-empty { display: flex; align-items: center; justify-content: center; }
.entry-placeholder { font-size: 30px; }
.entry-empty {
  flex-direction: column;
  gap: 6px;

  i { font-size: 25px; }
  strong { font-size: 13px; }
  small { color: #a0a7b2; font-size: 11px; }
}

.entry-overlay {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  height: 30px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #fff;
  background: rgba(25, 33, 45, .68);
  font-size: 12px;
}

.selected-summary {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #606266;
  font-size: 12px;

  > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}

.clear-button { flex: 0 0 auto; padding: 0; color: #f56c6c; font-size: 12px; }
.picker-toolbar { display: grid; grid-template-columns: minmax(240px, 1fr) 190px auto auto; gap: 10px; }
.picker-content { min-height: 492px; margin-top: 18px; }
.image-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }

.image-card {
  overflow: hidden;
  border: 2px solid transparent;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 0 0 1px #e4e7ed;
  cursor: pointer;
  transition: border-color .2s, box-shadow .2s, transform .2s;

  &:hover,
  &:focus { outline: none; box-shadow: 0 5px 16px rgba(31, 45, 61, .14); transform: translateY(-1px); }
  &.is-selected { border-color: #409eff; box-shadow: 0 0 0 2px rgba(64, 158, 255, .12); }
}

.image-card__preview {
  position: relative;
  display: flex;
  height: 164px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #b5bdc9;
  background: #f3f5f8;
  font-size: 34px;

  .el-image { width: 100%; height: 100%; }
}

.selected-mark {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  width: 25px;
  height: 25px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  background: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, .35);
}

.image-card__meta {
  display: grid;
  gap: 4px;
  padding: 10px 12px 11px;

  strong,
  span,
  small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  strong { color: #303133; font-size: 14px; }
  span,
  small { color: #909399; font-size: 12px; }
}

.empty-result {
  display: flex;
  min-height: 420px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #909399;

  i { font-size: 48px; }
  strong { color: #606266; font-size: 15px; }
  span { font-size: 12px; }
}

.picker-pagination {
  display: flex;
  min-height: 34px;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  color: #909399;
  font-size: 12px;
}

@media (max-width: 960px) {
  .picker-toolbar { grid-template-columns: 1fr 180px; }
  .image-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
